import { InferenceIcon } from "@/components/Icons/InferenceIcon";
import { SpannerIcon } from "@/components/Icons/SpannerIcon";
import { DEFAULT_PAGINATION_LIMIT } from "@/constants";
import env from "@/env";
import {
  getFormikEnvKeysValues,
  buildCaasHealthCheckForApi,
  parseCaasDurationToSeconds
} from "@/lib/utils";
import { getAuthContext } from "@/store/auth";
import { getClusterStatusMapEntryFromResult } from "@/store/cluster";
import { useUserStore } from "@/store/user";
import { createMockContainerLogStream } from "@/utils/mockContainerLogs";
import {
  ClusterForm,
  ClusterHardwareInfo,
  ClusterHardwareInfoMap,
  ClusterType,
  ContainerEvent,
  ContainerHardware,
  ContainerHardwareResponse,
  ContainerLocation,
  DynamicSupplyHardware,
  DynamicSupplyHardwareFiltersResponse,
  DynamicSupplyHardwareResponse,
  HealthCheck,
  KubernetesHardware,
  KubernetesHardwareLocation,
  KubernetesNodeHardwareResponse,
  NormalEnvKeys,
  NormalEnvValues,
  SecretEnvKeys,
  SecretEnvValues,
  SimpleCluster,
  SimpleClusterDetail,
  SimpleClusterDetailResponse,
  SimpleClusterForm,
  SimpleClusterHardware,
  SimpleClusterHardwareInfoResponse,
  SimpleClusterHardwareResponse,
  SimpleClusterLocation,
  SimpleClusterResponse,
  SimpleClusterWorker,
  SimpleClusterWorkerInfo,
  SimpleClusterWorkerInfoResponse,
  SimpleClusterWorkerResponse,
  VirtualMachineHardware,
  VirtualMachineHardwareResponse,
  VmHardwareLocation
} from "@/types";
import { filterNull } from "@/utils";
import { executeAPIRequest } from "@/utils/api";
import { getCurrencyParam } from "@/utils/credits";
import { isPaygCluster } from "@/utils/payg";
import { getHardware } from "@/utils/mapping";
import { errorIndicatesRequiresLogin, isPurchasedCreditsExempt } from "@/utils/user";
import { getGoogleAnalyticsHeaders } from "@/utils/analytics";
import { stringify } from "qs";
import { createContext } from "react";
import { create } from "zustand";

type UseSimpleClusterStoreProps = {
  createCluster: (values: SimpleClusterForm) => Promise<string | undefined>;
  fetchClusterHardwareInfo: (
    values: Partial<SimpleClusterForm>
  ) => Promise<ClusterHardwareInfo | null>;
  fetchDynamicSupplyHardwareAndFilters: (options?: {
    abortController?: AbortController;
    search?: string;
    filters?: Partial<DynamicSupplyHardwareFiltersResponse>;
  }) => Promise<{
    filters: DynamicSupplyHardwareFiltersResponse;
    hardware: DynamicSupplyHardware[];
  }>;
  fetchSimpleClusterRequest: (options: {
    status: string;
    page?: number;
    limit?: number;
  }) => Promise<{
    resultCount: number;
    results: SimpleCluster[];
    statuses: string[];
  }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDeployment: (deploymentId: string) => Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  adjustDeployment: (values: SimpleClusterForm) => Promise<any>;
  removeCluster: (id: string) => Promise<void>;
  fetchSimpleClusterDetail: (options: { id: string }) => Promise<SimpleClusterDetail>;
  fetchSimpleClusterWorkerInfo: (options: {
    id: string;
    status: string;
    page?: number;
    limit?: number;
  }) => Promise<{
    resultCount: number;
    results: SimpleClusterWorker[];
    statuses: [];
  }>;
  fetchJobs: (deploymentId: string, containerId: string) => Promise<SimpleClusterWorkerInfo>;
  fetchLocations: () => Promise<SimpleClusterLocation[]>;
  fetchLocationsByHardware: (
    values: Partial<SimpleClusterForm>,
    options?: { abortController?: AbortController }
  ) => Promise<ContainerLocation[]>;
  fetchHardware: (
    values: Partial<SimpleClusterForm>
  ) => Promise<{ total: number; hardware: SimpleClusterHardware[] }>;
  fetchAvailableVms: () => Promise<{ total: number; hardware: VirtualMachineHardware[] }>;
  fetchLocationsByVmHardware: (
    values: Partial<SimpleClusterForm>,
    options?: { abortController?: AbortController }
  ) => Promise<VmHardwareLocation[]>;
  fetchAllHardware: () => Promise<ContainerHardware[]>;
  // Kubernetes specific
  fetchAvailableNodes: () => Promise<{ total: number; hardware: KubernetesHardware[] }>;
  fetchLocationsByNodeHardware: (
    values: Partial<SimpleClusterForm>,
    options?: { abortController?: AbortController }
  ) => Promise<KubernetesHardwareLocation[]>;
  fetchLogs: (
    deploymentId: string,
    containerId: string,
    allocationId: string | undefined,
    stream: "stdout" | "stderr",
    abortController?: AbortController
  ) => Promise<ReadableStream>;
};

const hardwareType = "gpu";
export const durations = [
  { duration: "hourly", percentage: 1 },
  {
    duration: "daily",
    percentage: 0.9
  },
  {
    duration: "weekly",
    percentage: 0.8
  },
  {
    duration: "monthly",
    percentage: 0.7
  }
];

export const CONNECTIVITY_TIER_OPTIONS = [
  {
    id: "1",
    downloadSpeedMbps: 100,
    name: "Low Speed",
    uploadSpeedMbps: 10
  },
  {
    id: "2",
    downloadSpeedMbps: 400,
    name: "Medium Speed",
    uploadSpeedMbps: 300
  },
  {
    id: "3",
    downloadSpeedMbps: 800,
    name: "High Speed",
    uploadSpeedMbps: 600
  },
  {
    id: "4",
    downloadSpeedMbps: 1600,
    name: "Ultra High Speed",
    uploadSpeedMbps: 1200
  }
];

export const KUBERNETES_CLUSTER_TYPES = [
  {
    id: "inference",
    name: "Inference",
    disabled: true,
    description: "Production ready clusters for low latency inference and heavy workloads",
    icon: InferenceIcon,
    validate: (values: Partial<ClusterForm>) => {
      if (values.nvLink && values.supplier === "2") {
        return false;
      }
      return true;
    }
  },
  {
    id: "train",
    name: "Train",
    description: "Production ready clusters for machine learning  models training and fine tuning",
    icon: SpannerIcon,
    validate: (values: Partial<ClusterForm>) => {
      if (values.supplier === "2") {
        return false;
      }
      return true;
    }
  }
];

export const createClusterData = {
  clusterTypes: [],
  locations: [],
  services: [],
  suppliers: [],
  hardwareTypes: [],
  hardwareManufacturers: [],
  hardwares: {},
  securityCompliances: [],
  hardwareList: [],
  durations,
  connectivityTiers: CONNECTIVITY_TIER_OPTIONS
};

export const newInitialFormValues = {
  name: "",
  clusterType: "general",
  kubeRay: false,
  supplier: "1",
  securityCompliance: "",
  service: "",
  connectivityTier: "",
  hardwareManufacturer: "",
  hardwareType,
  durationQuantity: 1,
  durationType: "hourly",
  hardware: "",
  locations: [],
  sustainable: false,
  masterNode: "1",
  currency: "stripe",
  createClusterData
} as SimpleClusterForm;

export const getClusterHourCount = (paymentQuantity: number, duration: string) => {
  switch (duration) {
    case "hourly":
      return paymentQuantity;
    case "daily":
      return 24 * paymentQuantity;
    case "weekly":
      return 7 * 24 * paymentQuantity;
    case "monthly":
      return 4 * 7 * 24 * paymentQuantity;
  }
};

export const createSimpleClusterStore = (clusterType: ClusterType | string) =>
  create<UseSimpleClusterStoreProps>(() => ({
    removeCluster: async (id: string) => {
      await executeAPIRequest<{
        data: SimpleClusterWorkerInfoResponse;
        status: string;
      }>({
        method: "delete",
        url:
          clusterType === "caas" || clusterType === ClusterType.VMAAS
            ? `/io-cloud/${clusterType}/deployment/${id}`
            : `/io-cloud/${clusterType}/cluster/${id}`
      });
    },
    fetchHardware: async (values: Partial<SimpleClusterForm>) => {
      const response = await executeAPIRequest<{
        data: {
          total: number;
          hardware: SimpleClusterHardwareResponse[];
        };
      }>({
        method: "get",
        url: `/io-cloud/${clusterType}/hardware?${stringify(
          filterNull({
            connectivity_tier: values.connectivityTier,
            ...(values.locations?.length
              ? {
                  location_ids: `[${values.locations?.join(",")}]`
                }
              : {})
          })
        )}`
      });

      return {
        total: response.data.total,
        hardware: response.data.hardware?.map((item) => normaliseSimpleClusterHardware(item))
      };
    },
    fetchDynamicSupplyHardwareAndFilters: async (options?: {
      abortController?: AbortController;
      search?: string;
      filters?: Partial<DynamicSupplyHardwareFiltersResponse>;
    }) => {
      // MOCK Implementation
      // const { search, filters, abortController } = options || {};
      // const { getMockDynamicSupply } = await import(
      //   "@/components/ClustersPage/CreateVirtualMachineFormV2/mockDynamicSupply"
      // );
      // const { hardware, filters: filtersResponse } = await getMockDynamicSupply({
      //   search,
      //   filters,
      //   abortSignal: abortController?.signal
      // });

      // return {
      //   hardware: hardware?.map((item) => normaliseDynamicSupplyHardware(item)),
      //   filters: filtersResponse
      // };

      // API Implementation
      const { abortController, search, filters } = options || {};
      const params: Record<string, unknown> = {
        ...(search ? { gpu: search } : {})
      };
      if (filters) {
        const {
          gpu_family,
          regions,
          supplier,
          gpu_memory,
          cpu_cores,
          device_memory,
          storage,
          confidential_compute
        } = filters;
        Object.assign(
          params,
          filterNull({
            gpu_family: Array.isArray(gpu_family) ? gpu_family : undefined,
            regions: Array.isArray(regions) ? regions.join(",") : undefined,
            supplier: Array.isArray(supplier) ? supplier.join(",") : undefined,
            gpu_memory_min: gpu_memory?.min ? gpu_memory.min : undefined,
            gpu_memory_max: gpu_memory?.max ? gpu_memory.max : undefined,
            cpu_cores_min: cpu_cores?.min ? cpu_cores.min : undefined,
            cpu_cores_max: cpu_cores?.max ? cpu_cores.max : undefined,
            device_memory_min: device_memory?.min ? device_memory.min : undefined,
            device_memory_max: device_memory?.max ? device_memory.max : undefined,
            storage_min: storage?.min ? storage.min : undefined,
            storage_max: storage?.max ? storage.max : undefined,
            confidential_compute: Array.isArray(confidential_compute)
              ? confidential_compute.length === 1
                ? confidential_compute[0]
                : undefined
              : undefined
          })
        );
      }
      const response = await executeAPIRequest<{
        data: {
          filters: DynamicSupplyHardwareFiltersResponse;
          hardware: DynamicSupplyHardwareResponse[];
        };
      }>({
        method: "get",
        url: `/io-cloud/${clusterType}/hardware?${stringify(params, { arrayFormat: "repeat" })}`,
        options: {
          abortController,
          publicError: true
        }
      });

      return {
        hardware: response.data.hardware?.map((item) => normaliseDynamicSupplyHardware(item)),
        filters: response.data.filters
      };
    },
    fetchLocations: async () => {
      const response = await executeAPIRequest<{
        data: {
          total: number;
          locations: SimpleClusterLocation[];
        };
      }>({
        method: "get",
        url: `/io-cloud/${clusterType}/locations`
      });
      return response.data.locations;
    },
    fetchJobs: async (deploymentId: string, containerId: string) => {
      const response = await executeAPIRequest<{
        data: SimpleClusterWorkerInfoResponse;
      }>({
        method: "get",
        url: `/io-cloud/${clusterType}/deployment/${deploymentId}/containers-jobs/${containerId}`
      });

      return normaliseSimpleClusterWorkerInfo(response.data, clusterType);
    },
    fetchSimpleClusterWorkerInfo: async (options) => {
      const { id, status = "all", page = 1, limit = 20 } = options;

      const response = await executeAPIRequest<{
        data: SimpleClusterWorkerInfoResponse;
      }>({
        method: "get",
        url:
          clusterType === "caas"
            ? `/io-cloud/${clusterType}/deployment/${id}/containers?${stringify({
                ...(status != "all" ? { status } : {}),
                page: page,
                page_size: limit
              })}`
            : clusterType === ClusterType.VMAAS
              ? `/io-cloud/${clusterType}/deployment/${id}/vms?${stringify({
                  ...(status != "all" ? { status } : {}),
                  page: page,
                  page_size: limit
                })}`
              : clusterType === ClusterType.KUBERNETES
                ? `/io-cloud/${clusterType}/deployment/${id}/nodes?${stringify({
                    ...(status != "all" ? { status } : {}),
                    page: page,
                    page_size: limit
                  })}`
                : `/io-cloud/${clusterType}/cluster/${id}/pods?${stringify({
                    ...(status != "all" ? { status } : {}),
                    page: page,
                    page_size: limit
                  })}`
      });

      const { data } = response;
      const result = normaliseSimpleClusterWorkerInfo(data, clusterType);
      const additionalData: Record<string, unknown> = {};
      if (typeof data.gpu_count === "number") {
        additionalData["totalGpu"] = data.gpu_count;
      }

      if (typeof data.cpu_count === "number") {
        additionalData["totalCpu"] = data.cpu_count;
      }

      return {
        resultCount: data.total,
        results: result.items,
        statuses: [],
        additionalData: additionalData
      };
    },
    fetchSimpleClusterRequest: async (options) => {
      try {
        const { status = "all", page = 1, limit = DEFAULT_PAGINATION_LIMIT } = options;

        const { isLoggedIn, token } = getAuthContext();

        if (!isLoggedIn || !token) {
          return {
            resultCount: 0,
            results: [],
            statuses: []
          };
        }

        const response = await executeAPIRequest<{
          data: {
            clusters: SimpleClusterResponse[];
            total: number;
            statuses: string[];
          };
        }>({
          method: "get",
          url:
            clusterType === "caas" || clusterType === ClusterType.VMAAS
              ? `/io-cloud/${clusterType}/deployment?${stringify({
                  ...(status != "all" ? { status } : {}),
                  page: page,
                  page_size: limit
                })}`
              : `/io-cloud/${clusterType}/cluster?${stringify({
                  ...(status != "all" ? { status } : {}),
                  page: page,
                  page_size: limit
                })}`
        });

        const { data } = response;

        const results = data.clusters.map((cluster) => {
          return { ...normaliseSimpleCluster(cluster) };
        });

        return {
          resultCount: data.total,
          results,
          statuses: data.statuses
        };
      } catch (e) {
        if (errorIndicatesRequiresLogin(e)) {
          return {
            resultCount: 0,
            results: [],
            statuses: []
          };
        }
        throw e;
      }
    },
    fetchSimpleClusterDetail: async ({ id }) => {
      const response = await executeAPIRequest<{
        data: SimpleClusterDetailResponse;
      }>({
        method: "get",
        url:
          clusterType === "caas" || clusterType === ClusterType.VMAAS
            ? `/io-cloud/${clusterType}/deployment/${id}`
            : `/io-cloud/${clusterType}/cluster/${id}`
      });

      return normaliseSimpleClusterDetail(response.data, clusterType);
    },

    fetchAllHardware: async () => {
      const response = await executeAPIRequest<{
        data: {
          total: number;
          hardware: ContainerHardwareResponse[];
        };
      }>({
        method: "get",
        url: `/io-cloud/caas/hardware/max-gpus-per-container`
        // url: `/io-cloud/caas/hardware/max-gpus-per-container?${stringify(
        //   filterNull({ connectivity_tier: values.connectivityTier })
        // )}`
      });

      return response.data.hardware?.map((item) => normaliseContainerHardware(item));
    },

    fetchLocationsByHardware: async (values: Partial<SimpleClusterForm>, options) => {
      const { abortController } = options || {};
      const caasGpusPerContainer = clusterType === "caas" ? values.gpusPerContainer : undefined;
      const hardware_qty =
        caasGpusPerContainer || values.hardwareQuantity || values.hardwareInfo?.quantity;
      const response = await executeAPIRequest<{
        data: ContainerLocation[];
      }>({
        method: "get",
        url: `/io-cloud/caas/available-replicas?${stringify(
          filterNull({
            connectivity_tier: values.connectivityTier,
            hardware_id: values.hardware,
            ...(hardware_qty ? { hardware_qty } : {})
          })
        )}`,
        options: {
          abortController
        }
      });

      return response.data;
    },

    fetchClusterHardwareInfo: async (values: Partial<SimpleClusterForm>) => {
      const caasGpusPerContainer = clusterType === "caas" ? values.gpusPerContainer : undefined;
      const hardware_qty =
        caasGpusPerContainer || values.hardwareQuantity || values.hardwareInfo?.quantity; //TODO remove

      const parsedReplicaCount = parseInt(String(values.replicaCount ?? ""), 10);
      const hasValidReplicaCount = Number.isFinite(parsedReplicaCount) && parsedReplicaCount >= 1;

      // CaaS price API requires replica_count — never call without a valid value
      // (e.g. while the user has cleared the replicas field mid-edit).
      if (clusterType === "caas" && !hasValidReplicaCount) {
        return null;
      }

      const response = await executeAPIRequest<{
        data: SimpleClusterHardwareInfoResponse;
      }>({
        method: "get",
        url: `/io-cloud/${clusterType}/price?${stringify(
          filterNull({
            ...(clusterType === ClusterType.BARE_METAL
              ? { hardware_ids: `[${values.hardware}]` }
              : { hardware_id: values.hardware }),
            // hardware_ids: `[${values.hardware}]`,
            connectivity_tier: values.connectivityTier,
            service_id: values.service,
            ...(values.deploymentId ? { deploy_id: values.deploymentId } : {}),
            ...(hasValidReplicaCount ? { replica_count: parsedReplicaCount } : {}),
            ...(values.durationType ? { duration_type: values.durationType } : {}),
            ...(values.durationQuantity ? { duration_qty: values.durationQuantity } : {}),
            ...(hardware_qty ? { hardware_qty } : {}),
            ...(values.currency ? { currency: getCurrencyParam(values.currency) } : {}),
            // A PAYG cluster is always charged from credits, so it is priced as crypto
            // no matter how the user tops those credits up (stripe included).
            ...(isPaygCluster(clusterType)
              ? { payment_method: "crypto" }
              : values.currency
                ? { payment_method: values.currency === "stripe" ? "fiat" : "crypto" }
                : {}),
            ...(values.locations?.length || 0 > 0
              ? {
                  location_ids: `[${values.locations?.join(",")}]`
                }
              : {}),
            ...(clusterType === ClusterType.KUBERNETES
              ? {
                  gpus_per_node: values.gpusPerNode,
                  duration_hours: values.durationQuantity,
                  nodes_quantity: hasValidReplicaCount ? parsedReplicaCount : 1
                }
              : {})
          })
        )}`
      });

      return normaliseSimpleClusterHardwareInfo(response.data);
    },

    getDeployment: async (deploymentId: string) => {
      const response = await executeAPIRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any;
      }>({
        method: "get",
        url: `/io-cloud/caas/deployment/${deploymentId}`
      });

      return response;
    },

    adjustDeployment: async (values: SimpleClusterForm) => {
      const env_variables: Record<string, string> = {};
      const secret_env_variables: Record<string, string> = {};
      const { normKeys, normValues, secretKeys, secretValues } = getFormikEnvKeysValues(values);
      if (secretKeys && secretKeys?.length > 0 && secretKeys.length === secretValues?.length) {
        secretKeys.forEach(
          (key, idx) =>
            (secret_env_variables[values[key as keyof SecretEnvKeys]] =
              values[secretValues?.[idx] as keyof SecretEnvValues])
        );
      }
      if (normKeys && normKeys?.length > 0 && normKeys.length === normValues?.length) {
        normKeys.forEach(
          (key, idx) =>
            (env_variables[values[key as keyof NormalEnvKeys]] =
              values[normValues?.[idx] as keyof NormalEnvValues])
        );
      }

      const healthCheck = buildCaasHealthCheckForApi(values);

      const data = {
        env_variables,
        secret_env_variables,
        entrypoint: values.command ? JSON.parse(values.command) : [],
        traffic_port: values.trafficPort,
        image_url: values.containerImage,
        registry_username: values.username,
        registry_secret: values.secret,
        health_check: healthCheck ?? null
        // args: [],
        // command: ''
      };

      const response = await executeAPIRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any;
      }>({
        method: "patch",
        url: `/io-cloud/caas/deployment/${values.containerDeploymentId}`,
        options: {
          data,
          timeout: env.API_DEPLOY_CLUSTER_TIMEOUT
        }
      });

      return response;
    },

    createCluster: async (values: SimpleClusterForm) => {
      const isPayg = isPaygCluster(clusterType);
      let data: Record<string, unknown> = {
        resource_private_name: values.name,
        hardware_qty: values.hardwareQuantity,
        hardware_id: [values.hardware],
        quantity: values.hardwareQuantity,
        location_id: values.locations,
        connectivity_tier: clusterType === ClusterType.BARE_METAL ? values.connectivityTier : 4,
        user_id: useUserStore.getState().userId,
        currency: getCurrencyParam(values.currency),
        payment_source:
          values.currency === "credits" ||
          values.currency === "stripe" ||
          values.currency === "usdc"
            ? "credits"
            : "payment",
        duration_qty: values.durationQuantity,
        duration_type: values.durationType,
        transaction: values.hardwareInfo?.transaction,
        resource_id: values.resourceId,
        deploy_id: values.deploymentId
      };

      if (isPayg) {
        // PAYG deploys carry no rental window: the API prepays the first hour from
        // credits and the billing cron charges usage from there.
        delete data.duration_qty;
        delete data.duration_type;
        data.billing_model = "payg";
        data.payment_source = "credits";
      }

      if (clusterType === "caas") {
        const env_variables: Record<string, string> = {};
        const secret_env_variables: Record<string, string> = {};
        const { normKeys, normValues, secretKeys, secretValues } = getFormikEnvKeysValues(values);
        if (secretKeys && secretKeys?.length > 0 && secretKeys.length === secretValues?.length) {
          secretKeys.forEach(
            (key, idx) =>
              (secret_env_variables[values[key as keyof SecretEnvKeys]] =
                values[secretValues?.[idx] as keyof SecretEnvValues])
          );
        }
        if (normKeys && normKeys?.length > 0 && normKeys.length === normValues?.length) {
          normKeys.forEach(
            (key, idx) =>
              (env_variables[values[key as keyof NormalEnvKeys]] =
                values[normValues?.[idx] as keyof NormalEnvValues])
          );
        }

        const healthCheck = buildCaasHealthCheckForApi(values);

        data = {
          ...data,
          hardware_qty: values.gpusPerContainer || values.hardwareQuantity,
          hardware_id: values.hardware,
          ...(values.supplier === "external" && values.gpuUnavailabilityPolicy
            ? { on_incomplete_booking: values.gpuUnavailabilityPolicy }
            : {}),
          registry_config: {
            image_url: values.containerImage,
            registry_username: values.isPrivate ? values.username : "",
            registry_secret: values.isPrivate ? values.secret : ""
          },
          container_config: {
            replica_count: parseInt(values.replicaCount ?? "", 10),
            env_variables,
            secret_env_variables,
            entrypoint: values.command ? JSON.parse(values.command) : null,
            traffic_port: values.trafficPort,
            health_check: healthCheck ?? null
          }
        };
      }

      if (clusterType === ClusterType.VMAAS) {
        const vmSshKeys: Record<string, string> = {};
        if (values.sshType === "manual") {
          values.sshKeys?.forEach((sshKey) => {
            if (sshKey.key && sshKey.value) {
              vmSshKeys[sshKey.key.trim()] = sshKey.value.trim();
            }
          });
        }

        const vmsNetworkServices: Record<string, Record<string, string | number | string[]>> = {};
        if ((values.networkServices?.length || 0) > 0) {
          values.networkServices?.forEach((service) => {
            vmsNetworkServices[service.name] = {
              port: service.port,
              protocol: service.protocol,
              whitelist: service.whitelistIps.filter((ip) => ip.trim() !== "")
            };
          });
        }

        data = {
          ...data,
          connectivity_tier: parseInt(values.connectivityTier ?? "", 10),
          hardware_qty: values.hardwareQuantity,
          hardware_id: values.hardware,
          vm_image_type: values.vmImageType,
          github_ids:
            values.sshType === "github" ? values.githubIds?.split(",").map((id) => id.trim()) : [],
          ssh_keys: vmSshKeys,
          vms_qty: parseInt(values.replicaCount ?? "", 10),
          network_services: vmsNetworkServices
        };
      }

      if (clusterType === ClusterType.KUBERNETES) {
        data = {
          ...data,
          hardware_qty: values.hardwareQuantity,
          hardware_id: values.hardware,
          gpus_per_node: values.gpusPerNode,
          nodes_quantity: parseInt(values.replicaCount ?? "", 10)
        };
      }

      const headers = {
        ...(values.hardwareInfo?.transaction
          ? { transaction: values.hardwareInfo?.transaction }
          : {}),
        ...getGoogleAnalyticsHeaders()
      };

      const response = await executeAPIRequest<{
        cluster_id: string;
        deployment_id?: string;
        status: string;
      }>({
        method: "post",
        url: `/io-cloud/${clusterType}/deploy`,
        options: {
          data,
          headers,
          timeout: env.API_DEPLOY_CLUSTER_TIMEOUT,
          publicError: true
        }
      });

      return response.deployment_id ?? response.cluster_id;
    },
    fetchLogs: async (deploymentId, containerId, allocationId, stream, abortController) => {
      try {
        const response = await executeAPIRequest<ReadableStream>({
          method: "get",
          url: `/io-cloud/caas/deployment/${deploymentId}/log/${containerId}?allocation_id=${allocationId}&stream=${stream}`,
          options: {
            adapter: "fetch",
            responseType: "stream",
            publicError: true,
            timeout: 600000,
            abortController
          }
        });
        return response;
      } catch (e) {
        const isCanceled =
          typeof e === "object" &&
          e !== null &&
          "name" in e &&
          ((e as { name?: string }).name === "CanceledError" ||
            (e as { name?: string }).name === "AbortError");
        if (env.MOCK_CONTAINER_LOGS && !isCanceled && !abortController?.signal.aborted) {
          console.warn("[MOCK_CONTAINER_LOGS] Falling back to fake log stream", e);
          return createMockContainerLogStream(stream, abortController);
        }
        throw e;
      }
    },
    fetchAvailableVms: async () => {
      const response = await executeAPIRequest<{
        data: {
          total: number;
          hardware: VirtualMachineHardwareResponse[];
        };
      }>({
        method: "get",
        url: "/io-cloud/vmaas/hardware/max-gpus-per-vm"
      });

      return {
        total: response.data.total,
        hardware: response.data.hardware?.map((item) => normaliseVirtualMachineHardware(item))
      };
    },
    // Kubernetes: list max GPUs per node (same shape as VMs)
    fetchAvailableNodes: async () => {
      const response = await executeAPIRequest<{
        data: {
          total: number;
          hardware: KubernetesNodeHardwareResponse[];
        };
      }>({
        method: "get",
        url: "/io-cloud/kubernetes/hardware/max-gpus-per-node"
      });

      return {
        total: response.data.total,
        hardware: response.data.hardware?.map((item) => normaliseKubernetesHardware(item))
      };
    },
    fetchLocationsByVmHardware: async (values: Partial<SimpleClusterForm>, options) => {
      const { abortController } = options || {};
      const hardware_qty = values.hardwareQuantity || values.hardwareInfo?.quantity;
      const response = await executeAPIRequest<{
        data: VmHardwareLocation[];
      }>({
        method: "get",
        url: `/io-cloud/vmaas/available-vms?${stringify(
          filterNull({
            connectivity_tier: values.connectivityTier,
            hardware_id: values.hardware,
            ...(hardware_qty ? { hardware_qty } : {})
          })
        )}`,
        options: {
          abortController
        }
      });

      return response.data;
    },
    // Kubernetes: locations by selected node hardware
    fetchLocationsByNodeHardware: async (values: Partial<SimpleClusterForm>, options) => {
      const { abortController } = options || {};
      const gpus_per_node = values.hardwareQuantity || values.hardwareInfo?.quantity;
      const response = await executeAPIRequest<{
        data: KubernetesHardwareLocation[];
      }>({
        method: "get",
        url: `/io-cloud/kubernetes/available-nodes?${stringify(
          filterNull({
            connectivity_tier: values.connectivityTier,
            hardware_id: values.hardware,
            ...(gpus_per_node ? { gpus_per_node } : {})
          })
        )}`,
        options: {
          abortController
        }
      });

      return response.data;
    }
  }));

const normaliseVirtualMachineHardware = (result: VirtualMachineHardwareResponse) => {
  const { hardware_id, hardware_name, brand_name, min_gpus_per_vm, max_gpus_per_vm, ...rest } =
    result;
  const hardwareManufacturer = brand_name.toLocaleUpperCase();
  const { hardwareManufacturerColor = "", hardwareManufacturerIcon = "" } =
    getHardware(hardwareManufacturer) || {};

  return {
    hardwareManufacturerColor,
    hardwareManufacturerIcon,
    brandName: brand_name,
    minGpusPerVm: typeof min_gpus_per_vm === "number" ? min_gpus_per_vm : 1,
    maxGpusPerVm: Math.min(rest.available, max_gpus_per_vm),
    ...rest,
    name: hardware_name,
    id: hardware_id,
    price: result.price
  } as VirtualMachineHardware;
};

const normaliseKubernetesHardware = (result: KubernetesNodeHardwareResponse) => {
  const { hardware_id, hardware_name, brand_name, min_gpus_per_node, max_gpus_per_node, ...rest } =
    result;

  const hardwareManufacturer = brand_name.toLocaleUpperCase();
  const { hardwareManufacturerColor = "", hardwareManufacturerIcon = "" } =
    getHardware(hardwareManufacturer) || {};

  return {
    hardwareManufacturerColor,
    hardwareManufacturerIcon,
    brandName: brand_name,
    minGpusPerNode: typeof min_gpus_per_node === "number" ? min_gpus_per_node : 1,
    maxGpusPerNode:
      typeof max_gpus_per_node === "number"
        ? Math.min(
            (rest as { available?: number }).available ?? max_gpus_per_node,
            max_gpus_per_node
          )
        : 1,
    ...rest,
    name: hardware_name,
    id: hardware_id,
    price: result.price
  } as KubernetesHardware;
};

const normaliseSimpleClusterHardwareInfo = (result: SimpleClusterHardwareInfoResponse) => {
  const transformedArray: ClusterHardwareInfoMap[] = [];

  // Prefer available_replica_count for replica/node steppers.
  // Do not treat available_quantities as replicas when available_replica_count is present
  // (even if it's a short list) — that list is request-scoped pricing inventory.
  if (Array.isArray(result?.available_replica_count) && result.available_replica_count.length > 0) {
    result.available_replica_count.forEach((value) =>
      transformedArray.push({ label: String(value), value: String(value) })
    );
  } else if (Array.isArray(result?.available_quantities)) {
    // Legacy fallback for flows that only return GPU/node quantity options.
    result.available_quantities.forEach((value) =>
      transformedArray.push({ label: String(value), value: String(value) })
    );
  }

  return {
    averagePerCard: result.avg,
    ioAveragePerCard: result.avg_iocoin,
    quantity: result.qty ?? result.hardware_qty,
    availableQuantity: transformedArray,
    conversionRate: result.conversion_rate,
    transaction: result.transaction,
    cost: result.gross_cost,
    totalCost: result.total_cost,
    providerFee: result.provider_fee,
    // CLO-658: internal/exempt users skip the purchased-credits floor entirely.
    minNonPromotionalCreditsRequired: isPurchasedCreditsExempt()
      ? 0
      : result.min_non_promotional_credits_required
  } as ClusterHardwareInfo;
};

const normaliseSimpleCluster = (result: SimpleClusterResponse) => {
  const hardware = getHardware(result.brand_name || "");
  const status = getClusterStatusMapEntryFromResult(result);
  return {
    ...(status ?? { status: result.status }),
    id: result.id,
    hardwareName: result.hardware_name,
    name: result.name,
    served: result.served,
    brandName: result.brand_name,
    remaining: result.remaining,
    hardwareQuantity: result.hardware_quantity,
    percentCompleted: result.completed_percent,
    computeMinutesRemaining: result.compute_minutes_remaining,
    billingModel: result.billing_model,
    ...hardware
  } as SimpleCluster;
};

const normaliseSimpleClusterDetail = (
  result: SimpleClusterDetailResponse,
  clusterType: ClusterType | string
) => {
  const status = getClusterStatusMapEntryFromResult(result);
  const clusterDetail = {
    ...(status ?? { status: result.status }),
    id: result.id,
    served: result.served,
    remaining: result.remaining,
    percentCompleted: result.completed_percent,
    startedAt: result.started_at,
    createdAt: result.created_at,
    finishesAt: result.finishes_at,
    finishedAt: result.finished_at,
    downloadSpeedMbps: result.download_speed_mbps,
    uploadSpeedMbps: result.upload_speed_mbps,
    amountPaid: result.amount_paid,
    name: result.name,
    hardwareQuantity: result.hardware_info?.length
      ? result.hardware_info?.reduce((acc, { quantity }) => acc + quantity, 0)
      : result.hardware_quantity ||
        result.gpus_per_container ||
        result.gpus_per_vm ||
        result.gpus_per_node,
    hardwareName: result.hardware_info?.length
      ? result.hardware_info?.[0].hardware_name
      : result?.hardware_name || "",
    baseTierName: result.base_tier_name,
    locations: result.locations
      ? result.locations.map((location) => {
          return {
            value: `${location.id}`,
            label: location.name,
            code: location.iso2
          };
        })
      : [],
    hardware: result.hardware_info
      ? result.hardware_info?.map(({ hardware_name, brand_name, quantity }) => ({
          name: hardware_name,
          brandName: brand_name,
          hardwareQuantity: quantity
        }))
      : {
          name: result.hardware_name,
          brandName: result.brand_name,
          hardwareQuantity: result.gpus_per_container || result.gpus_per_node || result.gpus_per_vm
        },
    currency: result.asset,
    totalCost: result.total_cost,
    billingModel: result.billing_model
  } as SimpleClusterDetail;

  if (clusterType === ClusterType.KUBERNETES) {
    return { ...clusterDetail, k8sFile: result.k8s_file ?? "" };
  }

  if (clusterType === "caas") {
    const healthCheckPath = result.container_config?.health_check?.path;
    const hasDeclaredHealthCheck =
      typeof healthCheckPath === "string" && healthCheckPath.trim().length > 0;

    return {
      ...clusterDetail,
      totalGpus: result.total_gpus,
      totalContainers: result.total_containers,
      gpusPerContainer: result.gpus_per_container,
      hasDeclaredHealthCheck
    };
  }

  if (clusterType === "vmaas") {
    return {
      ...clusterDetail,
      totalGpus: result.total_gpus,
      totalVms: result.total_vms,
      githubIds: result.github_ids || [],
      gpusPerVm: result.gpus_per_vm,
      vmImageType: result.vm_image_type,
      sshKeys: result.ssh_keys || {}
    };
  }

  return clusterDetail;
};

export const SimpleClusterResultsDataTableContext = createContext<{
  setSelectedCluster: React.Dispatch<React.SetStateAction<SimpleCluster | undefined>>;
  setSelectedAction: React.Dispatch<React.SetStateAction<string | undefined>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}>(undefined as any);

const normaliseSimpleClusterHardware = (result: SimpleClusterHardwareResponse) => {
  const { brand_name } = result;
  const hardwareManufacturer = brand_name.toLocaleUpperCase();
  const { hardwareManufacturerColor = "", hardwareManufacturerIcon = "" } =
    getHardware(hardwareManufacturer) || {};

  return {
    hardwareManufacturerColor,
    hardwareManufacturerIcon,
    brandName: brand_name,
    ...result
  } as SimpleClusterHardware;
};

const normaliseDynamicSupplyHardware = (result: DynamicSupplyHardwareResponse) => {
  const {
    id,
    name,
    price,
    vram_per_card,
    interconnect,
    nvlink,
    storage,
    vcpu,
    memory,
    num_cards,
    supplier,
    location,
    old_price,
    sold_out,
    deploy_id,
    confidential_compute
  } = result;
  return {
    hardwareId: id,
    hardwareName: name,
    price,
    vramPerCard: vram_per_card,
    interconnect,
    nvlink,
    storage,
    vCPU: vcpu,
    memory,
    numCards: num_cards,
    supplier,
    location,
    oldPrice: old_price,
    isSoldOut: sold_out,
    deploymentId: deploy_id ?? id,
    isConfidentialCompute: confidential_compute
  } as DynamicSupplyHardware;
};

const normaliseContainerHardware = (result: ContainerHardwareResponse) => {
  const {
    hardware_id,
    hardware_name,
    brand_name,
    max_gpus_per_container,
    min_gpus_per_container,
    ...rest
  } = result;
  const hardwareManufacturer = brand_name.toLocaleUpperCase();
  const { hardwareManufacturerColor = "", hardwareManufacturerIcon = "" } =
    getHardware(hardwareManufacturer) || {};

  return {
    hardwareManufacturerColor,
    hardwareManufacturerIcon,
    brandName: brand_name,
    maxGpusPerContainer: max_gpus_per_container,
    minGpusPerContainer: min_gpus_per_container,
    ...rest,
    name: hardware_name,
    id: hardware_id
  } as ContainerHardware;
};

const normaliseSimpleClusterWorkerInfo = (
  result: SimpleClusterWorkerInfoResponse,
  clusterType: ClusterType | string
) => {
  return {
    total: result.total,
    items: result.workers.map((worker) => {
      return normaliseSimpleClusterWorker(worker, clusterType);
    })
  } as SimpleClusterWorkerInfo;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const normaliseContainerDeploymentConfig = (config: any) => {
  let normalEnvKeys: NormalEnvKeys = {};
  let normalEnvValues: NormalEnvValues = {};
  if (config?.container_config?.env_variables) {
    Object.keys(config?.container_config?.env_variables).forEach((key, idx) => {
      normalEnvKeys = { ...normalEnvKeys, [`env_normal_key_${idx}`]: key };
      normalEnvValues = {
        ...normalEnvValues,
        [`env_normal_value_${idx}`]: config?.container_config?.env_variables[key]
      };
    });
  }

  let secretEnvKeys: SecretEnvKeys = {};
  let secretEnvValues: SecretEnvValues = {};
  if (config?.container_config?.secret_env_variables) {
    Object.keys(config?.container_config?.secret_env_variables).forEach((key, idx) => {
      secretEnvKeys = { ...normalEnvKeys, [`env_normal_key_${idx}`]: key };
      secretEnvValues = {
        ...normalEnvValues,
        [`env_normal_value_${idx}`]: config?.container_config?.secret_env_variables[key]
      };
    });
  }

  let healthCheck: Pick<
    SimpleClusterForm,
    | "healthCheckPath"
    | "healthCheckIntervalSeconds"
    | "healthCheckTimeoutSeconds"
    | "healthCheckGracePeriodSeconds"
  > | null = null;
  if (config?.container_config?.health_check) {
    healthCheck = {
      healthCheckPath:
        typeof config?.container_config?.health_check?.path === "string"
          ? config.container_config.health_check.path
          : "",
      healthCheckIntervalSeconds: parseCaasDurationToSeconds(
        config?.container_config?.health_check?.interval
      ),
      healthCheckTimeoutSeconds: parseCaasDurationToSeconds(
        config?.container_config?.health_check?.timeout
      ),
      healthCheckGracePeriodSeconds: parseCaasDurationToSeconds(
        config?.container_config?.health_check?.grace_period
      )
    };
  }

  const containerConfig = {
    name: config?.name,
    // connectivityTier: config?.base_tier_name,
    // durationQuantity?: number;
    // hardware: string;
    // hardwareManufacturer: string;
    // hardwareType: config?.hardware_info?.brand_name,
    // hardwareQuantity: config?.hardware_info?.quantity,
    locations: config?.locations?.[0]?.id,
    // hardwareInfo?: ClusterHardwareInfo;
    containerDeploymentId: config?.id,
    containerImage: config?.container_config?.image_url,
    command: config?.container_config?.entrypoint
      ? JSON.stringify(config?.container_config?.entrypoint)
      : "",
    trafficPort: config?.container_config?.traffic_port,
    currency: config?.asset,
    ...healthCheck,
    // secret?: string;
    ...normalEnvKeys,
    ...normalEnvValues,
    ...secretEnvKeys,
    ...secretEnvValues
  };
  return containerConfig as SimpleClusterForm;
};

const normaliseSimpleClusterWorker = (
  result: SimpleClusterWorkerResponse,
  clusterType: ClusterType | string
) => {
  const hardware = getHardware(result.brand_name || "");

  const baseWorker = {
    id: String(result.id),
    hardwareName: result.hardware,
    brandName: result.brand_name,
    hardwareQuantity: result.hardware_quantity,
    status: result.status,
    uptimePercent: result.uptime_percent,
    isExternal: result.is_external,
    ...hardware
  };

  if (ClusterType.KUBERNETES === clusterType) {
    return baseWorker;
  }

  if (clusterType === "caas") {
    const containerEvents: ContainerEvent[] = [];
    if (result.container_events && result.container_events?.length > 0) {
      result.container_events.forEach((item) =>
        containerEvents.push({
          message: item.message,
          time: item.time
        })
      );
    }

    return {
      containerEvents,
      containerId: result.container_id,
      allocationId: result.allocation_id,
      publicUrl: result.public_url,
      createdAt: result.created_at,
      externalCheckStatus: result.external_check_status ?? null,
      ...baseWorker
    };
  }

  if (clusterType === "vmaas") {
    const containerEvents: ContainerEvent[] = [];
    if (result.vm_events && result.vm_events?.length > 0) {
      result.vm_events.forEach((item) =>
        containerEvents.push({
          message: item.message,
          time: item.time
        })
      );
    }

    return {
      containerEvents,
      vmStatus: result.vm_status,
      vmId: result.vm_id,
      sshAccess: result.ssh_access,
      networkServices: result.network_services,
      ...baseWorker
    };
  }

  return {
    sshConnect: result.ssh_connect,
    sshLink: result.ssh_link,
    login: result.login,
    password: result.password,
    ...baseWorker
  } as SimpleClusterWorker;
};

export const useBareMetalStore = createSimpleClusterStore(ClusterType.BARE_METAL);
export const useKubernetesStore = createSimpleClusterStore(ClusterType.KUBERNETES);
export const useContainerStore = createSimpleClusterStore("caas");
export const useVirtualMachineStore = createSimpleClusterStore(ClusterType.VMAAS);
